import { useState, useMemo, Component } from 'react';
import { Search, X, ArrowUpRight, ArrowDownRight, ChevronUp, ChevronDown, Loader2, AlertTriangle } from 'lucide-react';
import { colors, fonts } from '../styles/theme';

// ============================================================
// Badge
// ============================================================
export function Badge({ children, color, bg, style }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600,
      fontFamily: fonts.body, letterSpacing: '0.03em', textTransform: 'uppercase',
      color: color || colors.accent, backgroundColor: bg || colors.accentDim,
      ...style,
    }}>
      {children}
    </span>
  );
}

// ============================================================
// Button
// ============================================================
export function Button({ children, variant = 'default', size = 'md', onClick, style, icon: Icon }) {
  const variants = {
    primary: { bg: colors.accent, color: '#ffffff', hoverBg: colors.accentHover },
    secondary: { bg: colors.bgCard, color: colors.accent, border: `1px solid ${colors.accent}40` },
    default: { bg: colors.bgCard, color: colors.text, border: `1px solid ${colors.border}` },
    ghost: { bg: 'transparent', color: colors.textSecondary },
    danger: { bg: 'rgba(239,68,68,0.1)', color: colors.danger, border: `1px solid rgba(239,68,68,0.2)` },
  };
  const sizes = {
    sm: { padding: '5px 10px', fontSize: 12 },
    md: { padding: '8px 16px', fontSize: 13 },
    lg: { padding: '10px 20px', fontSize: 14 },
  };
  const v = variants[variant];
  const s = sizes[size];
  return (
    <button
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, borderRadius: 6, fontWeight: 600,
        fontFamily: fonts.body, cursor: 'pointer', border: v.border || 'none',
        background: v.bg, color: v.color, ...s, transition: 'all 0.15s', ...style,
      }}
    >
      {Icon && <Icon size={size === 'sm' ? 13 : 15} />}
      {children}
    </button>
  );
}

// ============================================================
// SearchBar
// ============================================================
export function SearchBar({ value, onChange, placeholder }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, background: colors.bgCard,
      border: `1px solid ${colors.border}`, borderRadius: 8, padding: '8px 14px', flex: 1, maxWidth: 360,
    }}>
      <Search size={16} color={colors.textSecondary} />
      <input
        type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder || 'Search...'}
        style={{
          background: 'none', border: 'none', outline: 'none', color: colors.text,
          fontSize: 13, fontFamily: fonts.body, width: '100%',
        }}
      />
      {value && (
        <X size={14} color={colors.textSecondary} style={{ cursor: 'pointer' }} onClick={() => onChange('')} />
      )}
    </div>
  );
}

// ============================================================
// StatCard
// ============================================================
export function StatCard({ icon: Icon, label, value, change, changeType, onClick, active }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: colors.bgCard,
        border: `1px solid ${active ? colors.accent : colors.border}`,
        borderRadius: 8, padding: '20px 24px', flex: 1, minWidth: 200,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        boxShadow: active ? `0 0 0 1px ${colors.accent}` : 'none',
      }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        {Icon && (
          <div style={{
            width: 36, height: 36, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: colors.accentDim,
          }}>
            <Icon size={18} color={colors.accent} />
          </div>
        )}
        {change && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 2, fontSize: 12, fontWeight: 600,
            color: changeType === 'up' ? colors.success : colors.danger,
          }}>
            {changeType === 'up' ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
            {change}
          </div>
        )}
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, fontFamily: fonts.heading, color: colors.text, marginBottom: 4 }}>
        {value}
      </div>
      <div style={{ fontSize: 13, color: colors.textSecondary, fontFamily: fonts.body }}>
        {label}
      </div>
    </div>
  );
}

// ============================================================
// Table — with column sorting
// Columns can define `sortValue: (row) => comparable` to enable sorting.
// ============================================================
export function Table({ columns, data, onRowClick, initialSortKey = null, initialSortDir = 'asc' }) {
  const [sortKey, setSortKey] = useState(initialSortKey);
  const [sortDir, setSortDir] = useState(initialSortDir);

  const handleHeaderClick = (col) => {
    if (!col.sortValue) return;
    if (sortKey === col.key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(col.key);
      setSortDir('asc');
    }
  };

  const sorted = useMemo(() => {
    if (!sortKey) return data;
    const col = columns.find((c) => c.key === sortKey);
    if (!col?.sortValue) return data;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...data].sort((a, b) => {
      const av = col.sortValue(a);
      const bv = col.sortValue(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'string') return dir * av.localeCompare(bv);
      return dir * (av - bv);
    });
  }, [data, sortKey, sortDir, columns]);

  return (
    <div style={{ overflowX: 'auto', border: `1px solid ${colors.border}`, borderRadius: 8 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: fonts.body, fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
            {columns.map((col) => {
              const sortable = !!col.sortValue;
              const active = sortKey === col.key;
              return (
                <th
                  key={col.key}
                  onClick={() => handleHeaderClick(col)}
                  style={{
                    padding: '12px 16px', textAlign: 'left', color: active ? colors.text : colors.textSecondary,
                    fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em',
                    background: colors.bgCard, whiteSpace: 'nowrap',
                    cursor: sortable ? 'pointer' : 'default', userSelect: sortable ? 'none' : 'auto',
                    transition: 'color 0.15s',
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                    {col.label}
                    {sortable && active && (
                      sortDir === 'asc'
                        ? <ChevronUp size={12} style={{ opacity: 0.8 }} />
                        : <ChevronDown size={12} style={{ opacity: 0.8 }} />
                    )}
                    {sortable && !active && (
                      <ChevronUp size={10} style={{ opacity: 0.2 }} />
                    )}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, i) => (
            <tr
              key={row.id || i}
              onClick={() => onRowClick?.(row)}
              style={{
                borderBottom: `1px solid ${colors.border}`,
                cursor: onRowClick ? 'pointer' : 'default',
                background: i % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.02)',
                transition: 'background 0.1s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = colors.bgHover}
              onMouseLeave={(e) => e.currentTarget.style.background = i % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.02)'}
            >
              {columns.map((col) => (
                <td key={col.key} style={{ padding: '12px 16px', color: colors.text, whiteSpace: 'nowrap' }}>
                  {col.render ? col.render(row) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================
// ErrorBoundary
// ============================================================
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{
          padding: 32, textAlign: 'center', color: colors.danger,
          background: colors.bgCard, border: `1px solid ${colors.border}`,
          borderRadius: 8, margin: 24,
        }}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Something went wrong</div>
          <div style={{ fontSize: 13, color: colors.textSecondary, fontFamily: fonts.mono, whiteSpace: 'pre-wrap', textAlign: 'left' }}>
            {this.state.error.message}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ============================================================
// Spinner  —  replaces 30+ inline <Loader2> + <style> keyframe combos
// Usage:  <Spinner />  or  <Spinner size={14} />  (inline button)
//         <Spinner fullPage />  (centred 100vh loader)
// ============================================================
export function Spinner({ size = 24, color: c, fullPage, style }) {
  const icon = <Loader2 size={size} color={c || colors.accent} className="spin" style={style} />;
  if (!fullPage) return icon;
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: colors.bg }}>
      {icon}
    </div>
  );
}

// ============================================================
// ModalOverlay  —  backdrop + centred card
// Usage:
//   <ModalOverlay onClose={close}>
//     <form style={{ maxWidth: 480 }}> ... </form>
//   </ModalOverlay>
// Or with maxWidth shorthand:
//   <ModalOverlay onClose={close} maxWidth={520}> ... </ModalOverlay>
// ============================================================
export function ModalOverlay({ children, onClose, maxWidth }) {
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 99 }} />
      <div style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, pointerEvents: 'none' }}>
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 12,
            padding: 32, width: '100%', maxWidth: maxWidth || 520,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)', pointerEvents: 'auto',
            maxHeight: '85vh', overflowY: 'auto',
          }}
        >
          {children}
        </div>
      </div>
    </>
  );
}

// ============================================================
// ConfirmDialog  —  title + message + Cancel / Confirm
// Usage:
//   <ConfirmDialog
//     title="Delete contact?"
//     message="This cannot be undone."
//     onCancel={() => setOpen(false)}
//     onConfirm={handleDelete}
//     confirmLabel="Delete"
//     confirmVariant="danger"
//   />
// ============================================================
export function ConfirmDialog({ title, message, onCancel, onConfirm, confirmLabel = 'Confirm', confirmVariant = 'primary', loading }) {
  return (
    <ModalOverlay onClose={onCancel} maxWidth={400}>
      <h3 style={{ fontFamily: fonts.heading, fontSize: 16, color: colors.text, marginBottom: 12 }}>{title}</h3>
      {message && <p style={{ fontSize: 13, color: colors.textSecondary, marginBottom: 20, lineHeight: 1.5 }}>{message}</p>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Button variant="default" onClick={onCancel}>Cancel</Button>
        <Button variant={confirmVariant} onClick={onConfirm}>
          {loading ? <Spinner size={14} color="#fff" /> : confirmLabel}
        </Button>
      </div>
    </ModalOverlay>
  );
}

// ============================================================
// ErrorBanner  —  inline error message block
// Usage:  {error && <ErrorBanner message={error} />}
//         {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
// ============================================================
export function ErrorBanner({ message, onDismiss, style }) {
  if (!message) return null;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '8px 12px', borderRadius: 6,
      background: 'rgba(239,68,68,0.1)', color: colors.danger, fontSize: 13,
      ...style,
    }}>
      <AlertTriangle size={14} style={{ flexShrink: 0 }} />
      <span style={{ flex: 1 }}>{message}</span>
      {onDismiss && (
        <X size={14} style={{ cursor: 'pointer', flexShrink: 0 }} onClick={onDismiss} />
      )}
    </div>
  );
}

// ============================================================
// PageHeader  —  consistent page title + subtitle + optional actions
// Usage:
//   <PageHeader title="Contacts" subtitle="Manage your contacts">
//     <Button variant="primary" icon={Plus}>Add Contact</Button>
//   </PageHeader>
// ============================================================
export function PageHeader({ title, subtitle, children, style }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, ...style }}>
      <div>
        <h1 style={{ fontFamily: fonts.heading, fontSize: 28, fontWeight: 700, color: colors.text, marginBottom: 4 }}>
          {title}
        </h1>
        {subtitle && (
          <p style={{ fontSize: 14, color: colors.textSecondary }}>{subtitle}</p>
        )}
      </div>
      {children && <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>{children}</div>}
    </div>
  );
}
